import { useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";

const CHECK_EVERY_MS = 15 * 60 * 1000;
const MAIN_RE = /\/static\/js\/main\.([a-f0-9]+)\.js/;

const loadedHash = () => {
  for (const s of document.scripts) {
    const m = s.src.match(MAIN_RE);
    if (m) return m[1];
  }
  return null;
};

async function latestHash() {
  const res = await fetch(`/index.html?__v=${Date.now()}`, { cache: "no-store" });
  const m = (await res.text()).match(MAIN_RE);
  return m ? m[1] : null;
}

async function applyUpdate() {
  try {
    const keys = await caches.keys();
    await Promise.all(keys.map((k) => caches.delete(k)));
    const reg = await navigator.serviceWorker?.getRegistration();
    await reg?.update();
  } catch { /* still reload */ }
  window.location.reload();
}

// Shows a bar when a newer deploy is live (compares the built bundle hash).
export default function UpdateBar() {
  const [available, setAvailable] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const current = loadedHash();
    if (!current) return undefined;
    const check = async () => {
      try {
        const next = await latestHash();
        if (next && next !== current) setAvailable(true);
      } catch { /* offline */ }
    };
    const onVisible = () => { if (document.visibilityState === "visible") check(); };
    const t = setInterval(check, CHECK_EVERY_MS);
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", check);
    check();
    return () => { clearInterval(t); document.removeEventListener("visibilitychange", onVisible); window.removeEventListener("focus", check); };
  }, []);

  if (!available) return null;
  return (
    <div className="fixed inset-x-0 bottom-0 z-[60] px-3 pb-[calc(env(safe-area-inset-bottom)+12px)]" data-testid="update-bar">
      <div className="mx-auto flex max-w-md items-center gap-3 rounded-2xl bg-slate-900 px-4 py-3 text-white shadow-2xl">
        <RefreshCw className={`h-4 w-4 shrink-0 ${busy ? "animate-spin" : ""}`} />
        <p className="flex-1 text-sm">A new version of SnapList is ready.</p>
        <button type="button" onClick={() => { setBusy(true); applyUpdate(); }}
          className="min-h-[36px] rounded-full bg-white px-4 text-sm font-semibold text-slate-900 transition-transform active:scale-95"
          data-testid="update-bar-apply">
          Update
        </button>
      </div>
    </div>
  );
}
